// Tiny reactive localStorage store backed by useSyncExternalStore so every
// screen (Home, My List, details, player) stays in sync without a state library.
//
// getSnapshot must return a STABLE reference between changes or React will
// re-render forever, so the parsed value is cached per raw localStorage string.
const listeners = new Map(); // key -> Set<listener>

export function createStore(key, fallback) {
  let cachedRaw = null;
  let cachedValue = fallback;

  const read = () => {
    try {
      const raw = localStorage.getItem(key);
      if (raw === cachedRaw) return cachedValue;
      cachedRaw = raw;
      cachedValue = raw ? JSON.parse(raw) : fallback;
    } catch {
      // Storage may be unavailable (private mode / quota) — keep last value.
    }
    return cachedValue;
  };

  const write = (value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage may be unavailable — app keeps working in memory.
    }
    cachedRaw = null; // force re-read on next getSnapshot
    cachedValue = value;
    const set = listeners.get(key);
    if (set) set.forEach((fn) => fn());
  };

  return {
    subscribe(listener) {
      if (!listeners.has(key)) listeners.set(key, new Set());
      listeners.get(key).add(listener);
      return () => {
        const set = listeners.get(key);
        if (set) {
          set.delete(listener);
          if (set.size === 0) listeners.delete(key);
        }
      };
    },
    getSnapshot: read,
    set(value) {
      write(value);
    },
    update(fn) {
      write(fn(read()));
    },
  };
}